import { getFormProps } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { type SEOHandle } from '@nasa-gcn/remix-seo';
import * as E from '@react-email/components';
import { type ActionFunctionArgs } from '@remix-run/node';
import { useFetcher } from '@remix-run/react';
import { clsx } from 'clsx';
import he from 'he';
import { useEffect } from 'react';
import { Trans } from 'react-i18next';
import { typedjson } from 'remix-typedjson';
import { HoneypotInputs } from 'remix-utils/honeypot/react';
import { z } from 'zod';
import { useEditableTranslation, type TranslateRawFunc } from '#common/i18n/edit-mode.tsx';
import { sendEmail } from '#common/services/email.server';
import { checkHoneypot } from '#common/utils/honeypot.server';
import { PrefixWithEnv, useTrackedForm } from '#common/utils/misc';
import { emailSchema } from '#common/utils/user-validation';
import { Button } from '#speaker-shop/components/Button';
import i18nServer from '#speaker-shop/i18n/i18n.server.ts';

function contactFormSchema(t: TranslateRawFunc) {
    return z.object({
        name: z.string().max(50, { message: t('name-too-long') }).optional(),
        email: emailSchema(t),
        message: z.string({ required_error: t('message-required') }).min(20, { message: t('message-too-short') }).max(
            500,
            { message: t('message-too-long') }
        ),
        fixedBodyPart: z.string().optional(),
        topic: z.string().optional()
    });
}

export async function action({ request }: ActionFunctionArgs) {
    const locale = await i18nServer.getLocale(request);
    const t = await i18nServer.getFixedT(locale, 'audio-generation');

    const formData = await request.formData();
    await checkHoneypot(formData);

    const url = new URL(request.url);
    const shopSettingsUrl = new URL('/shop-settings', url.origin).toString();

    const submission = parseWithZod(formData, { schema: contactFormSchema(t) });
    if (submission.status !== 'success') {
        return typedjson(
            { result: submission.reply() },
            { status: submission.status === 'error' ? 400 : 200 }
        );
    }
    const result = await sendEmail({
        to: process.env.CONTACT_FORM_EMAIL!,
        subject: PrefixWithEnv('CrispyVoice: New contact form submission'),
        react: <ContactFormEmail {...submission.value} shopUrl={url.origin} shopSettingsUrl={shopSettingsUrl} />
    });

    if (result.status === 'success') {
        return typedjson({ result: 'success' });
    }
    console.error('Failed to send contact form email:', result.error);
    return typedjson(result, { status: 500 });
}

function ContactFormEmail(
    data: { shopUrl: string; shopSettingsUrl: string } & z.infer<ReturnType<typeof contactFormSchema>>,
) {
    return (
        <E.Tailwind>
            <E.Html lang='en' dir='ltr'>
                <E.Preview>From {data.email}: {data.message}</E.Preview>
                <E.Body>
                    <E.Container>
                        <E.Heading>New contact form submission</E.Heading>
                        <E.Text>
                            Someone just contacted you via your{' '}
                            <E.Link href={data.shopUrl}>CrispyVoice shop page</E.Link>:
                        </E.Text>
                        <E.Container>
                            <E.Row className='w-full'>
                                <E.Column className='w-1/4 align-top'>
                                    <E.Text>Name:</E.Text>
                                </E.Column>
                                <E.Column>
                                    <E.Text>{data.name || 'No name given'}</E.Text>
                                </E.Column>
                            </E.Row>
                            <E.Row className='w-full'>
                                <E.Column className='w-1/4 align-top'>
                                    <E.Text>Email:</E.Text>
                                </E.Column>
                                <E.Column>
                                    <E.Text>{data.email}</E.Text>
                                </E.Column>
                            </E.Row>
                            {data.topic && (
                                <E.Row className='w-full'>
                                    <E.Column className='w-1/4 align-top'>
                                        <E.Text>Topic:</E.Text>
                                    </E.Column>
                                    <E.Column>
                                        <E.Text>{data.topic}</E.Text>
                                    </E.Column>
                                </E.Row>
                            )}
                            <E.Row className='w-full'>
                                <E.Column className='w-1/4 align-top'>
                                    <E.Text>Message:</E.Text>
                                </E.Column>
                                <E.Column>
                                    <E.Text>{data.message}</E.Text>
                                </E.Column>
                            </E.Row>
                            {data.fixedBodyPart && (
                                <E.Row className='w-full'>
                                    <E.Column className='w-1/4 align-top'>
                                        <E.Text>Additional info:</E.Text>
                                    </E.Column>
                                    <E.Column>
                                        {he.decode(data.fixedBodyPart).split('\n').map((line, index) => (
                                            <E.Text key={index}>{line}</E.Text>
                                        ))}
                                    </E.Column>
                                </E.Row>
                            )}
                        </E.Container>
                        <E.Container>
                            <E.Text className='text-sm'>
                                This is a transactional email. You're receiving it because you own a CrispyVoice account
                                and someone just messaged you via your shop page.
                            </E.Text>
                            <E.Text className='text-sm'>
                                You can change the email address in your{' '}
                                <E.Link href={data.shopSettingsUrl}>account</E.Link>.
                            </E.Text>
                        </E.Container>
                    </E.Container>
                </E.Body>
            </E.Html>
        </E.Tailwind>
    );
}

export default function ContactForm({ topic, fixedBodyPart, onSendSuccessful, appearance = 'legacy' }: {
    topic: string;
    fixedBodyPart?: string;
    onSendSuccessful?: () => void;
    appearance?: 'legacy' | 'unified';
}) {
    const actionDataFetcher = useFetcher();
    const actionData = actionDataFetcher.data as any;
    const { t, rawT } = useEditableTranslation('contact');
    const ContactFormSchema = contactFormSchema(rawT);
    const [ form, fields ] = useTrackedForm({
        name: 'contact',
        constraint: getZodConstraint(ContactFormSchema),
        lastResult: actionDataFetcher.state === 'idle' ? actionData : null,
        onValidate: ({ formData }) => parseWithZod(formData, { schema: ContactFormSchema }),
        shouldValidate: 'onBlur',
        shouldRevalidate: 'onInput',
        shouldDirtyConsider(name: string) {
            return name !== 'intent';
        }
    });
    const unified = appearance === 'unified';
    const fieldClassName = unified
        ? 'core-input block w-full rounded-md p-2 outline-none'
        : 'rounded-lg border border-gray-300 bg-white block w-full p-2';

    useEffect(() => {
        if (actionData?.result === 'success') {
            if (onSendSuccessful) {
                onSendSuccessful();
            }
        }
    }, [ actionData, onSendSuccessful ]);

    return (
        <actionDataFetcher.Form
            className='space-y-3 justify-center items-center'
            method='POST'
            action='/resources/contact-form'
            {...getFormProps(form)}
        >
            <HoneypotInputs />

            <div>
                <label htmlFor='name' className='sr-only'>{t('name')}</label>
                <input
                    id='name'
                    type='text'
                    name='name'
                    placeholder={rawT('name')}
                    className={clsx(
                        fieldClassName,
                        !fields.name.valid && 'border-2 border-red-500'
                    )}
                />
                {fields.name.valid
                    ? null
                    : fields.name.errors?.map(x => <div key={x} className='text-red-400 font-bold mt-1'>{x}</div>)}
            </div>
            <div>
                <label htmlFor='email' className='sr-only'>{t('email')}</label>
                <input
                    id='email'
                    type='email'
                    name='email'
                    placeholder={rawT('email')}
                    className={clsx(
                        fieldClassName,
                        !fields.email.valid && 'border-2 border-red-500'
                    )}
                />
                {fields.email.valid
                    ? null
                    : fields.email.errors?.map(x => <div key={x} className='text-red-400 font-bold mt-1'>{x}</div>)}
            </div>
            <div>
                <label htmlFor='message' className='sr-only'>{t('message')}</label>
                <textarea
                    id='message'
                    name='message'
                    className={clsx(
                        fieldClassName,
                        !fields.message.valid && 'border-2 border-red-500'
                    )}
                    placeholder={rawT('message')}
                    rows={4}
                >
                </textarea>
                {fields.message.valid
                    ? null
                    : fields.message.errors?.map(x => <div key={x} className='text-red-400 font-bold mt-1'>{x}</div>)}
            </div>
            <input type='hidden' name='topic' value={topic} />
            {fixedBodyPart && <input type='hidden' name='fixedBodyPart' value={fixedBodyPart} />}

            <div className='flex-col flex gap-3 mt-10 sm:flex-row'>
                {actionData?.result !== 'success' && (
                    <Button
                        color={unified ? 'slate' : 'white'}
                        size='normal'
                        type='submit'
                        aria-label='Submit'
                        className={clsx(
                            'w-full disabled:cursor-not-allowed cursor-pointer',
                            unified ? 'core-primary-button border-0' : 'border border-gray-300 hover:bg-gray-50'
                        )}
                        disabled={!form.dirty || !form.valid}
                    >
                        {actionDataFetcher.state === 'idle' ? t('submit') : 'Submitting...'}
                    </Button>
                )}
            </div>
            {actionData && actionData.result !== 'success'
                ? (
                    <div
                        className={clsx(
                            'p-4 rounded-xl mt-6',
                            unified ? 'core-booking-speaker' : 'border-2 border-black bg-white'
                        )}
                    >
                        <h3 className='text-lg lg:text-xl font-medium'>
                            <Trans t={rawT} i18nKey='error'>
                                <a href='mailto:welcome@crispyvoice.com'></a>
                            </Trans>
                        </h3>
                    </div>
                )
                : null}
            {actionData?.result === 'success'
                ? (
                    <div
                        className={clsx(
                            'p-4 rounded-xl mt-6',
                            unified ? 'core-booking-speaker' : 'border-2 border-black bg-white'
                        )}
                    >
                        <h3 className='text-lg lg:text-xl font-medium text-green-500'>{t('success')}</h3>
                    </div>
                )
                : null}
        </actionDataFetcher.Form>
    );
}

export const handle: SEOHandle = {
    getSitemapEntries: () => null // <- exclude this route from sitemap
};
